/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */

// Core imports
import { Fragment, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

// Component imports
import { Button, Input, Popconfirm, Skeleton, Space, Table, Tag, Tooltip, Typography } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import Highlighter from 'react-highlight-words';
import BotAvatar from '../UI/BotAvatar';
import ResponsiveTagsList from '../UI/ResponsiveTagsList';
import PredefinedEntityModal from '../Modals/PredefinedEntityModal';
import { EditIcon } from '../Icons/EditIcon';
import { DeleteIcon } from '../Icons/DeleteIcon';
import { PlusIcon } from '../Icons/PlusIcon';

// Misc imports
import { isEmptyArray } from '../../utils/is/isEmptyArray';
import { useFetchOrganizationAutomationsQuery, useFetchOrganizationPredefinedResponsesQuery } from '../../store/liveChatSettings/liveChatSettingsApi';
import { getCurrentQueryState } from '../../store/utils/getCurrentQueryState';
import { useDeleteAutomationMutation } from '../../store/predefinedEntities/predefinedEntitiesApi';
import { useDragDetection } from '../../utils/hooks/useDragDetection';
import { DEFAULT_PAGINATION_SIZE } from '../../constants/main';
import useTableFilterIcon from '../../utils/hooks/useTableFilterIcon';

const LiveChatAutomationsSettingsTable = () => {
  const { orgId } = useParams();
  const { t } = useTranslation();
  const { handleMouseDown, isDragging } = useDragDetection();

  const [isModalOpen, setIsModalOpen] = useState();
  const [mode, setMode] = useState();
  const [selectedItem, setSelectedItem] = useState();
  const [searchFilter, setSearchFilter] = useState('');
  const [pageSize, setPageSize] = useState(DEFAULT_PAGINATION_SIZE);

  const { data: organizationAutomations, isFetching: isFetchingAutomations } = useFetchOrganizationAutomationsQuery({ orgId }, { skip: !orgId });
  const { data: organizationPredefinedResponses } = useFetchOrganizationPredefinedResponsesQuery({ orgId }, { skip: !orgId });
  const { data: bots, isFetching: isFetchingBots } = getCurrentQueryState('fetchBots', { orgId }, !orgId);
  const [deleteOrganizationAutomation] = useDeleteAutomationMutation();

  const onEditClick = (record) => {
    setMode('automations');
    setSelectedItem(record);
    setIsModalOpen(true);
  };

  const handleTableChange = (pagination, _filters, _sorter) => {
    setPageSize(pagination.pageSize);
  };

  return (
    <Fragment>
      <Space
        size={16}
        align="center"
        className="justify-content-between mb-4 w-100"
        wrap
      >
        <Input
          size="large"
          placeholder={t('Search in contents')}
          allowClear
          disabled={isFetchingAutomations}
          prefix={<SearchOutlined className="text-muted" />}
          onChange={(e) => setSearchFilter(e.target.value)}
        />

        <Button
          key="addResponse"
          type="primary"
          icon={<PlusIcon />}
          className="d-block ml-auto"
          size="large"
          onClick={() => {
            setMode('automations');
            setSelectedItem();
            setIsModalOpen(true);
          }}
        >
          {t('Add Automation')}
        </Button>
      </Space>

      <Table
        rowKey="id"
        size="small"
        loading={!organizationAutomations || (isFetchingBots && !bots)}
        dataSource={!searchFilter ? organizationAutomations : organizationAutomations?.filter(i => i.name.toLowerCase().includes(searchFilter.toLowerCase()) || i.url?.toLowerCase().includes(searchFilter.toLowerCase()))}
        pagination={{
          pageSize: pageSize,
          showSizeChanger: true,
          size: 'default',
          showTotal: (total) => `${t('Total Automations')}: ${total}`
        }}
        rowClassName="cursor-pointer"
        onRow={(record) => ({
          onMouseDown: handleMouseDown,
          onClick: () => {
            if(!isDragging()) {
              onEditClick(record);
            }
          }
        })}
        onChange={handleTableChange}
        columns={[
          {
            key: 'buttonTitle',
            dataIndex: 'name',
            title: t('Button Title'),
            sorter: (a, b) => a.name.localeCompare(b.name),
            ellipsis: true,
            render: (_text) => (
              <Highlighter
                highlightClassName="bg-highlight p-0"
                searchWords={[searchFilter]}
                autoEscape
                textToHighlight={(_text)}
              />
            )
          },
          {
            title: t('Categories'),
            dataIndex: 'categories',
            key: 'categories',
            render: (_text, record) => {
              if (!record.categories || isEmptyArray(record.categories)) {
                return '-';
              }

              return (
                <ResponsiveTagsList
                  values={record.categories.map((category, index) => ({ label: category, value: index }))}
                  mode="tags"
                  className="w-100"
                  tagRender={(tag) => <span className="ant-select-selection-item p-0 px-1 mr-2">{tag.label}</span>}
                />
              );
            },
            filters: organizationPredefinedResponses ? organizationPredefinedResponses.reduce((acc, curr) => {
              if (!curr.categories || isEmptyArray(curr.categories)) {
                return acc;
              }
              return acc
                .concat(curr.categories.map((category) => ({ text: category, value: category }))) // Flatten the array
                .filter((item, index, self) => self.findIndex((i) => i.value === item.value) === index); // Remove duplicates
            }
            , []) : [],
            onFilter: (value, record) => {
              if (!record.categories || isEmptyArray(record.categories)) {
                return false;
              }
              return record.categories.includes(value);
            },
            filterIcon: useTableFilterIcon
          },
          {
            key: 'actionUrl',
            dataIndex: 'url',
            title: t('Automation URL'),
            ellipsis: true,
            render: (value) => (
              <Highlighter
                highlightClassName="bg-highlight p-0"
                searchWords={[searchFilter]}
                autoEscape
                textToHighlight={(value)}
              >
                <Typography.Text copyable ellipsis className="w-100">
                  {value}
                </Typography.Text>
              </Highlighter>
            )
          },
          {
            key: 'visibility',
            dataIndex: 'organizationWide',
            title: t('Visibility'),
            responsive: ['md'],
            filters: [
              {
                text: 'All organization users',
                value: true
              },
              {
                text: 'Owner',
                value: false
              }
            ],
            onFilter: (value, record) => record.organizationWide === value,
            filterIcon: useTableFilterIcon,
            render: (value) => (
              value ? t('All organization users') : t('Owner')
            )
          },
          {
            key: 'bots',
            title: t('Agent Availability'),
            responsive: ['sm'],
            dataIndex: 'tenantIds',
            ellipsis: true,
            filterMultiple: true,
            filterSearch: (input, record) => {
              const botDisplayName = bots.items.find((bot) => bot.id === record.value).displayName.toLowerCase();
              return botDisplayName.includes(input.toLowerCase());
            },
            filters: bots?.items.map(bot => ({
              text: (
                <Space size={4} align="center" wrap={false}>
                  <BotAvatar bot={bot} />
                </Space>
              ),
              value: bot.id
            })),
            onFilter: (value, record) => {
              return record?.tenantIds?.includes(value);
            },
            filterIcon: useTableFilterIcon,
            render: record => {
              if (!record && isFetchingAutomations && isFetchingBots) {
                return <Skeleton.Button active size="small" />;
              }

              if (!record?.length > 0) {
                return t('All organization Agents');
              }

              const associatedBots = bots?.items?.filter(bot => record.includes(bot.id)) ?? [];

              return (
                <ResponsiveTagsList
                  values={
                    associatedBots.map((bot) => ({
                      icon: <BotAvatar avatarSize={24} bot={bot} showBotName={false} />,
                      label: bot.displayName,
                      value: bot.id
                    }))
                  }
                  maxTagCount={4}
                  tagRender={(tag) => {
                    const matchedTag = associatedBots.find(bot => bot.id === tag.value);
                    return (
                      <Tooltip overlayClassName="pointer-events-none" title={matchedTag.displayName}>
                        <BotAvatar avatarSize={24} bot={matchedTag} showBotName={false} className="mr-2" />
                      </Tooltip>
                    );
                  }}
                />
              );
            }
          },
          {
            key: 'automaticTrigger',
            title: t('Automatic Trigger'),
            responsive: ['xxl'],
            filters: [
              {
                text: 'On miss',
                value: 'miss'
              },
              {
                text: 'On end',
                value: 'end'
              },
              {
                text: 'None',
                value: 'none'
              }
            ],
            onFilter: (value, record) => {
              if (value === 'miss') {
                return record.runOnMissedRequest;
              }
              if (value === 'end') {
                return record.runOnEndConversation;
              }
              if (value === 'none') {
                return !record.runOnEndConversation && !record.runOnMissedRequest;
              }
            },
            filterIcon: useTableFilterIcon,
            render: (_value, record) => {
              const tags = [];
              if (record.runOnMissedRequest) {
                tags.push(
                  <Tag>
                    {t('On miss')}
                  </Tag>
                );
              }
              if (record.runOnEndConversation) {
                tags.push(
                  <Tag>
                    {t('On end')}
                  </Tag>
                );
              }

              if (tags.length) {
                return tags;
              }

              return '-';
            }
          },
          {
            key: 'actions',
            title: t('Actions'),
            align: 'center',
            width: 100,
            render: (_value, record) => (
              <Space wrap={false}>
                <Tooltip overlayClassName="pointer-events-none" title={t('Edit')}>
                  <Button
                    shape="circle"
                    type="text"
                    icon={<EditIcon />}
                    onClick={() => onEditClick(record)}
                  />
                </Tooltip>

                <Popconfirm
                  trigger="click"
                  title={t('Delete automation?')}
                  placement="topRight"
                  onConfirm={() => {
                    deleteOrganizationAutomation({ orgId, automationId: record.id });
                  }}
                  okText={t('Delete')}
                >
                  <Tooltip overlayClassName="pointer-events-none" title={t('Delete')}>
                    <Button
                      shape="circle"
                      type="text"
                      icon={<DeleteIcon />}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </Tooltip>
                </Popconfirm>
              </Space>
            )
          }
        ]}
      />

      <PredefinedEntityModal
        orgLevelForm
        selectedItem={selectedItem}
        setSelectedItem={setSelectedItem}
        open={isModalOpen}
        setIsOpen={setIsModalOpen}
        mode={mode}
      />
    </Fragment>
  );
};

export default LiveChatAutomationsSettingsTable;
